import { CrewUi } from 'corso-types';
import { z } from 'zod';
import { zfd } from 'zod-form-data';
import { draftClaimLineItemContext } from '~/app/features/crew/claim-line-items/claimLineItemMiddleware';
import { crewOrderContext } from '~/app/features/order-middleware/orderMiddleware';
import { settingsContext } from '~/app/settingsMiddleware';
import api from '~/utils/api';
import { ClaimType, ResolutionMethod } from '~/utils/constants';
import { makeAction, makeLoader, redirect } from '~/utils/routing';
import {
  advancedExchangeMethodSchema,
  claimLineItemSchema,
  exchangeVariantSchema,
} from '~/utils/schemas';

const exchangeRecommendationSchema =
  CrewUi.claimLineItemCreateSchema.shape.exchangeRecommendation.unwrap();

const paramsSchema = z.object({
  store: z.string(),
  idFromPlatform: z.string(),
  claimType: z.string(),
});

export const { loader, useLoaderData } = makeLoader(
  ({ context, params }, appStore) => {
    const { store, idFromPlatform, claimType } = paramsSchema.parse(params);
    const settings = context.get(settingsContext);
    const draftClaimLineItem = context.get(draftClaimLineItemContext);
    const order = context.get(crewOrderContext);

    const summaryPagePath = `/${store}/order/${idFromPlatform}/${claimType}`;

    const isWarranty = new RegExp(ClaimType.warranty, 'i').test(claimType);
    if (isWarranty) {
      const parsedClaim = claimLineItemSchema.parse({
        ...draftClaimLineItem,
        requestedResolutionMethodEnum: ResolutionMethod.warrantyReview,
      });

      appStore.dispatch(
        appStore.actions.completeDraftClaimLineItem(parsedClaim),
      );

      return redirect(summaryPagePath);
    }

    const { shouldPrioritizeCredit, creditDetailText, creditHeadlineText } =
      settings.theme;

    const { isRefundEnabled, isGiftCardEnabled, isExchangeEnabled } =
      settings.return;

    const {
      return: {
        isExchangeOnly,
        // isEligible - as in within the time window
        exchange: { isEligible: isExchangeEligible },
        refund: { isEligible: isRefundEligible },
        giftCard: { isEligible: isGiftCardEligible },
      },
      productIdFromPlatform,
    } = draftClaimLineItem.lineItem;

    // omitted by an automation based on the reason selected, despite being eligible
    const omitted = draftClaimLineItem.omittedResolutionMethods ?? [];

    const canExchange = isExchangeEnabled && isExchangeEligible;
    const canRefund =
      isRefundEnabled &&
      isRefundEligible &&
      !omitted.includes(ResolutionMethod.refund);
    const canGiftCard =
      isGiftCardEnabled &&
      isGiftCardEligible &&
      !omitted.includes(ResolutionMethod.giftCard);
    // ? does offerShopNow impact ability to credit?
    const canCredit = (canRefund || canGiftCard) && !isExchangeOnly;

    if (!canExchange && canCredit) {
      const claimLineItem = claimLineItemSchema.parse({
        ...draftClaimLineItem,
        requestedResolutionMethodEnum: canGiftCard
          ? ResolutionMethod.giftCard
          : ResolutionMethod.refund,
      });
      // set resolution
      appStore.dispatch(
        appStore.actions.completeDraftClaimLineItem(claimLineItem),
      );
      // redirect to claim line item summary page
      return redirect(summaryPagePath);
    }

    const exchangeData =
      canExchange && productIdFromPlatform
        ? api
            .fetchProductVariantInfo({
              params: {
                storefrontId: settings.storefrontId,
                idFromPlatform: productIdFromPlatform,
              },
              query: {
                countryCode: order.countryCode,
                isExchangeOfExchange: !!order.isExchangeOfExchange,
                reasonId: draftClaimLineItem.reasonId,
                reasonDetailId: draftClaimLineItem.reasonDetailId,
                variantIdFromPlatform:
                  draftClaimLineItem.lineItem.variantIdFromPlatform ??
                  undefined,
                comment: draftClaimLineItem.comments ?? undefined,
              },
            })
            .then((results) => ({
              ...results,
              products: results.products.map((result) => ({
                ...result,
                options: result.options.sort((a, b) => a.position - b.position),
              })),
            }))
        : Promise.resolve({
            advancedExchangeMethod: 'NO_PAYMENT_OR_REFUND' as const,
            products: [],
            filters: [],
            isFilterAndSearchEnabled: false,
            isNoAdjustmentVariantExchangeEnabled: false,
            exchangeRecommendation: undefined,
          });

    return exchangeData.then((data) => ({
      aiExchangeRecommendation: data.exchangeRecommendation,
      exchangeProducts: data.products,
      isNoAdjustmentVariantExchangeEnabled:
        data.isNoAdjustmentVariantExchangeEnabled,
      exchangeFilters: data.filters ?? [],
      isFilterAndSearchEnabled: data.isFilterAndSearchEnabled,
      draftClaimLineItem,
      advancedExchangeMethod: data.advancedExchangeMethod,
      canExchange: canExchange && data.products.length > 0,
      orderId: order.idFromPlatform,
      store: settings.storeSlug,
      shouldPrioritizeCredit: !!shouldPrioritizeCredit,
      creditTitle: creditHeadlineText ?? 'Not looking to exchange?',
      creditDetailText:
        creditDetailText ?? 'Please continue to finish your request.',
      canCredit,
      ...(canCredit && {
        creditResolutionMethod: canGiftCard
          ? ResolutionMethod.giftCard
          : ResolutionMethod.refund,
      }),
    }));
  },
);

export enum FormKey {
  resolution = 'resolutionMethod',
  variant = 'variant',
  advancedExchangeMethod = 'advancedExchangeMethod',
  exchangeRecommendation = 'exchangeRecommendation',
  shopNow = 'shopNow',
}

const formSchema = zfd.formData(
  z.discriminatedUnion(FormKey.resolution, [
    z.object({
      [FormKey.resolution]: z.literal(ResolutionMethod.variantExchange),
      [FormKey.advancedExchangeMethod]: zfd.text(advancedExchangeMethodSchema),
      [FormKey.variant]: zfd.json(exchangeVariantSchema),
      [FormKey.exchangeRecommendation]: zfd
        .json(exchangeRecommendationSchema)
        .optional(),
      [FormKey.shopNow]: z.never().optional(),
    }),
    z.object({
      [FormKey.resolution]: z.union([
        z.literal(ResolutionMethod.refund),
        z.literal(ResolutionMethod.giftCard),
      ]),
      [FormKey.variant]: zfd.text(z.never().optional()),
      [FormKey.advancedExchangeMethod]: zfd.text(z.never().optional()),
      // Persist the rec even when the customer picks credit instead of an exchange
      // — wasExchangeRecommendationAccepted will be false, but the rec itself is
      // useful diagnostic data for evaluating engine performance.
      [FormKey.exchangeRecommendation]: zfd
        .json(exchangeRecommendationSchema)
        .optional(),
      // treating shopNow as an array since it is a value on a button and iOS does some weird stuff
      // where it sends the value twice with the same name
      [FormKey.shopNow]: zfd
        .repeatableOfType(zfd.checkbox({ trueValue: 'true' }).optional())
        .optional(),
    }),
  ]),
);

export const { action, useActionResult } = makeAction(
  ({ context, formData, params }, appStore) => {
    const { store, idFromPlatform, claimType } = paramsSchema.parse(params);
    const draftClaimLineItem = context.get(draftClaimLineItemContext);
    const form = formSchema.safeParse(formData);

    if (!form.success) {
      throw new Error('Malformed form data', {
        cause: {
          error: form.error,
          entries: Array.from(formData.entries()),
        },
      });
    }

    const recommendation = form.data[FormKey.exchangeRecommendation];

    const recommendationFields = recommendation
      ? {
          exchangeRecommendation: recommendation,
          wasExchangeRecommendationAccepted:
            form.data[FormKey.resolution] === ResolutionMethod.variantExchange
              ? recommendation.variant.idFromPlatform ===
                form.data[FormKey.variant].idFromPlatform
              : false,
        }
      : {};

    const parsedClaim = claimLineItemSchema.parse({
      ...draftClaimLineItem,
      shopNow: form.data[FormKey.shopNow]?.every((v) => v) ?? false,
      requestedResolutionMethodEnum: form.data[FormKey.resolution],
      ...(form.data[FormKey.resolution] === ResolutionMethod.variantExchange
        ? {
            variant: form.data[FormKey.variant],
            grams: form.data[FormKey.variant].grams,
            advancedExchangeMethod: form.data[FormKey.advancedExchangeMethod],
            ...(draftClaimLineItem.exchangeRecommendation && {
              exchangeRecommendation: {
                ...draftClaimLineItem.exchangeRecommendation,
                wasAccepted:
                  draftClaimLineItem.exchangeRecommendation.variant
                    .idFromPlatform ===
                  form.data[FormKey.variant].idFromPlatform,
              },
            }),
          }
        : {
            variant: undefined,
            variantExchangeLineItem: undefined,
            advancedExchangeMethod: undefined,
            grams: draftClaimLineItem.lineItem.grams,
            ...(draftClaimLineItem.exchangeRecommendation && {
              exchangeRecommendation: {
                ...draftClaimLineItem.exchangeRecommendation,
                wasAccepted: false,
              },
            }),
          }),
      ...recommendationFields,
    });

    appStore.dispatch(appStore.actions.completeDraftClaimLineItem(parsedClaim));

    return redirect(`/${store}/order/${idFromPlatform}/${claimType}`);
  },
);
